import type { ReactNode } from 'react'
import { Loader } from '@systemeio/components'
import { useRouter } from 'next/router'
import { communityPostKeys } from 'modules/community/api/key-factory'
import * as postsApi from 'modules/community/api/posts-api'
import { usePostsCommentsInfo } from 'modules/community/api/posts-comments-info'
import { useDeletePost } from 'modules/community/api/use-delete-post'
import { useDeletePostAndBanUser } from 'modules/community/api/use-delete-post-and-ban'
import { usePostAnnounceList } from 'modules/community/api/use-post-announce-list'
import { useToggleLike } from 'modules/community/api/use-toggle-like'
import AddPost from 'modules/community/components/manage-post/create-post'
import { PostPinnedList } from 'modules/community/components/pinned-post/post-pinned-list'
import { PostAnnounceSkeleton } from 'modules/community/components/post-announce/components/post-announce-skeleton'
import PostAnnounce from 'modules/community/components/post-announce/post-announce'
import { ScrollToTopButton } from 'modules/community/components/scroll-to-top-button'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'

export function CommunityFeed(): ReactNode {
  const router = useRouter()
  const communityPath = router.query.communityPath ?? ''
  const { isUserMember } = useIsUserMember()
  const postsAnnounceList = usePostAnnounceList({
    queryKey: communityPostKeys.list(communityPath),
    fetchPosts: (lastPostId: number | null) =>
      postsApi.getPostsByLastId(lastPostId || 0, communityPath),
  })
  const postIds = postsAnnounceList.posts.map(post => post.id)
  const commentsInfoQuery = usePostsCommentsInfo({ postIds })
  const deletePost = useDeletePost()
  const toggleLike = useToggleLike()
  const deletePostAndBanUser = useDeletePostAndBanUser({
    communityPath,
  })

  return (
    <>
      {isUserMember && <AddPost />}
      {postsAnnounceList.isLoading && postsAnnounceList.posts.length === 0 && (
        <>
          <PostAnnounceSkeleton key={1} />
          <PostAnnounceSkeleton key={2} />
        </>
      )}
      <PostPinnedList />
      {postsAnnounceList.posts.map(post => (
        <PostAnnounce
          key={post.id}
          deletePost={() => deletePost(post.id)}
          deletePostAndBanUser={removeRecentActivities =>
            deletePostAndBanUser({ postId: post.id, userId: post.userId, removeRecentActivities })
          }
          post={post}
          handleLikeClick={() => toggleLike.mutate({ id: post.id, isLiked: !post.isLiked })}
          commentsInfo={commentsInfoQuery.data?.items}
          commentsInfoError={commentsInfoQuery.error}
        />
      ))}
      {postsAnnounceList.isFetchingNextPage && (
        <div className="flex justify-center">
          <Loader size="xl" color="secondary" />
        </div>
      )}
      <div ref={postsAnnounceList.sentinelRef} />
      <ScrollToTopButton />
    </>
  )
}
